import React, {
  useRef,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import CampaignForm from "../../components/Form/CampaignForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import "react-loading-skeleton/dist/skeleton.css";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { MoreVertical, X } from "lucide-react";
import { createPortal } from "react-dom";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";

const Campaigns = () => {
  const {
    campaigns,
    addCampaign,
    updateCampaign,
    deleteCampaign,
    loading,
    pagination,
    fetchCampaigns,
    campaignLoading,
  } = useCampaigns();

  const buttonRefs = useRef({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const { roles } = useContext(AuthContext);
  const isHR =
    roles.includes("HR") ||
    roles.includes("Manager") ||
    roles.includes("HOD") ||
    roles.includes("Admin");
  const isManager = roles.includes("Manager");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState("");
  const [campaignNames, setCampaignNames] = useState([]);
  const [loadingCampaignNames, setLoadingCampaignNames] = useState(false);

  // Search API states
  const searchTimeoutRef = useRef(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPagination, setSearchPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset to page 1 when search query or campaign filter changes
  useEffect(() => {
    if (
      currentPage !== 1 &&
      (debouncedSearchQuery.trim() || selectedCampaignFilter)
    ) {
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, selectedCampaignFilter]);

  // Get selected campaign name for filtering
  const selectedCampaignName = useMemo(() => {
    if (!selectedCampaignFilter) return null;
    const campaign = campaignNames.find(
      (c) => String(c.id) === String(selectedCampaignFilter)
    );
    return campaign?.campaign_name || null;
  }, [selectedCampaignFilter, campaignNames]);

  // Fetch campaign names from search API
  useEffect(() => {
    const fetchCampaignNames = async () => {
      try {
        setLoadingCampaignNames(true);
        const token = getToken();
        const response = await axios.get(
          "https://hrmspayroll.prepstripe.com/api/campaigns/search?per_page=100",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data?.success && response.data?.data?.data) {
          // Extract unique campaign names with their IDs
          const uniqueCampaigns = [];
          const seenNames = new Set();
          response.data.data.data.forEach((campaign) => {
            if (
              campaign.campaign_name &&
              !seenNames.has(campaign.campaign_name)
            ) {
              seenNames.add(campaign.campaign_name);
              uniqueCampaigns.push({
                id: campaign.id,
                campaign_name: campaign.campaign_name,
              });
            }
          });
          setCampaignNames(uniqueCampaigns);
        }
      } catch (error) {
        console.error("Error fetching campaign names:", error);
        setNotification({
          message: "Failed to load campaign names",
          type: "error",
        });
      } finally {
        setLoadingCampaignNames(false);
      }
    };
    fetchCampaignNames();
  }, []);

  // Search campaigns API call
  useEffect(() => {
    const searchCampaigns = async () => {
      // If no search query and no campaign filter, clear search results
      if (!debouncedSearchQuery.trim() && !selectedCampaignFilter) {
        setSearchResults(null);
        return;
      }

      try {
        setIsSearching(true);
        const token = getToken();
        const params = new URLSearchParams();

        // Add search query if present
        if (debouncedSearchQuery.trim()) {
          params.append("search", debouncedSearchQuery.trim());
        }

        // Add campaign_name filter if selected
        if (selectedCampaignName) {
          params.append("campaign_name", selectedCampaignName);
        }

        // Add pagination
        params.append("page", currentPage.toString());
        params.append("per_page", pagination.perPage.toString() || "10");

        const response = await axios.get(
          `https://hrmspayroll.prepstripe.com/api/campaigns/search?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data?.success) {
          const searchData = response.data.data;
          setSearchResults({
            data: searchData.data || [],
            pagination: {
              currentPage: searchData.current_page || 1,
              totalPages: searchData.last_page || 1,
              totalItems: searchData.total || 0,
              perPage: searchData.per_page || 10,
            },
          });
          setSearchPagination({
            currentPage: searchData.current_page || 1,
            totalPages: searchData.last_page || 1,
            totalItems: searchData.total || 0,
            perPage: searchData.per_page || 10,
          });
        } else {
          setSearchResults({
            data: [],
            pagination: {
              currentPage: 1,
              totalPages: 1,
              totalItems: 0,
              perPage: pagination.perPage || 10,
            },
          });
        }
      } catch (error) {
        console.error("Error searching campaigns:", error);
        setNotification({
          message: "Failed to search campaigns",
          type: "error",
        });
        setSearchResults({
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: pagination.perPage || 10,
          },
        });
      } finally {
        setIsSearching(false);
      }
    };

    searchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchQuery,
    selectedCampaignFilter,
    selectedCampaignName,
    currentPage,
  ]);

  // Dropdown management
  const handleClickOutside = useCallback((event) => {
    // Check if click is outside dropdown container and dropdown menu
    if (
      !event.target.closest(".dropdown-container") &&
      !event.target.closest(".dropdown-menu")
    ) {
      setOpenDropdownId(null);
    }
  }, []);

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates - position exactly below the button
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 150; // min-w-[150px]
      const dropdownHeight = 100; // estimated height

      // Position dropdown directly below the button, left-aligned
      let x = rect.left;
      let y = rect.bottom + 4; // 4px gap below button

      // Check if dropdown would go off-screen to the right
      if (x + dropdownWidth > window.innerWidth) {
        x = window.innerWidth - dropdownWidth - 8; // 8px margin from edge
      }

      // Check if dropdown would go off-screen to the bottom
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4; // Position above button
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }

      // Ensure dropdown doesn't go off-screen to the left
      if (x < 0) {
        x = 8;
      }

      // Ensure dropdown doesn't go off-screen to the top
      if (y < 0) {
        y = 8;
      }

      setDropdownCoords({ x, y });
    }
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  // copy to clipboard button
  const handleCopy = async (campaign_id) => {
    const domain = window.location.origin;
    try {
      await navigator.clipboard.writeText(`${domain}/campaign/${campaign_id}`);
      setCopied(campaign_id);
      setNotification({
        message: "Campaign link copied to clipboard!",
        type: "success",
      });
      setTimeout(() => {
        setCopied(false);
        setNotification("");
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      setNotification("Failed to copy the link. Please try again.");
    }
  };

  // Handler to update campaign status
  const handleStatusChange = useCallback(
    async (campaign, newStatus) => {
      try {
        const response = await updateCampaign({
          id: campaign.id,
          status: newStatus,
        });
        // console.log("response: ", response)
        if (response?.success) {
          setNotification({
            message: "Status updated successfully",
            type: "success",
          });
        } else {
          setNotification({
            message: response?.message || "Failed to update status",
            type: "error",
          });
        }
      } catch (error) {
        setNotification({ message: "Failed to update status", type: "error" });
      }
    },
    [updateCampaign]
  );

  const openModalForAdd = () => {
    setEditingCampaign(null);
    setErrors({});
    setIsModalOpen(true);
  };

  const openModalForEdit = (campaign) => {
    setEditingCampaign(campaign);
    setErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingCampaign(null);
    setErrors({});
  };

  const handleFormSubmit = async (campaign) => {
    try {
      if (editingCampaign) {
        await updateCampaign({ ...editingCampaign, ...campaign });
        setNotification({
          message: "Campaign updated successfully",
          type: "success",
        });
      } else {
        const response = await addCampaign(campaign);
        if (!response || response.success === false) {
          setNotification({
            message: `${
              response?.message || "Failed to add campaign. Please try again."
            }`,
            type: "error",
          });
          return;
        }
        setNotification({
          message: "Campaign added successfully",
          type: "success",
        });
      }
      closeModal();
    } catch (error) {
      if (error?.errors) {
        setErrors(error.errors);
        setNotification({
          message: error.errors,
          type: "error",
        });
      } else {
        setNotification({
          message: `"An unexpected error occurred:", ${error}`,
          type: "error",
        });
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCampaign(id);
      setNotification({
        message: "Campaign deleted successfully",
        type: "success",
      });
    } catch (error) {
      setNotification({
        message: "Failed to delete campaign",
        type: "error",
      });
    }
  };

  const handlePageChange = (newPage) => {
    // Use search pagination if search is active, otherwise use context pagination
    const activePagination = searchResults ? searchPagination : pagination;
    if (newPage >= 1 && newPage <= activePagination.totalPages) {
      setCurrentPage(newPage);
      // Only fetch from context if not searching
      if (!searchResults) {
        fetchCampaigns(newPage);
      }
    }
  };

  // Determine which data source to use
  const displayCampaigns = useMemo(() => {
    const data = searchResults?.data || campaigns;
    return data ? [...data].reverse() : [];
  }, [searchResults, campaigns]);

  // Determine which pagination to use
  const activePagination = useMemo(() => {
    return searchResults ? searchPagination : pagination;
  }, [searchResults, searchPagination, pagination]);

  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => {
      const baseColumns = [
        {
          label: "Sr. No",
          field: "sr_no",
          render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
        },
        {
          label: "Campaign Name",
          field: "campaign_name",
          render: (row) => {
            const campaignDetailsPath = getRoleBasedRoute(
              `/campaigns/${row.id}`,
              roles
            );
            return (
              <div className="flex items-center gap-1">
                <Link
                  to={campaignDetailsPath}
                  className="text-blue-600 hover:underline dark:text-white hover:underline-offset-4 cursor-pointer"
                  aria-label={`View details for ${row.campaign_name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {row.campaign_name}
                </Link>
              </div>
            );
          },
        },
        {
          label: "Copy Link",
          field: "copy_link",
          render: (row) => (
            <button
              title="Copy campaign link"
              onClick={() => handleCopy(row.id)}
              className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-blue-600 transition-colors disabled:opacity-60 cursor-pointer"
              disabled={copied === row.id}
            >
              {copied === row.id ? "Copied" : "Copy Link"}
            </button>
          ),
        },
        {
          label: "Status",
          field: "status",
          render: (row) => {
            const status = row.status || "";
            let bgColor = "bg-blue-500"; // Default for other statuses

            if (status.toLowerCase() === "expired") {
              bgColor = "bg-gray-500";
            } else if (status.toLowerCase() === "on hold") {
              bgColor = "bg-red-500";
            } else if (status.toLowerCase() === "open") {
              bgColor = "bg-green-500";
            } else if (
              status.toLowerCase() === "close" ||
              status.toLowerCase() === "closed"
            ) {
              bgColor = "bg-gray-500";
            } else if (status.toLowerCase() === "active") {
              bgColor = "bg-green-500";
            }

            return (
              <span
                className={`${bgColor} text-white px-3 py-1 rounded-full text-xs font-medium`}
              >
                {status || "-"}
              </span>
            );
          },
        },
        {
          label: "Applications",
          field: "applications_count",
          render: (row) => (
            <span className="text-sm text-gray-800 dark:text-white">
              {typeof row.applications_count === "number"
                ? row.applications_count
                : row.applications_count
                ? row.applications_count
                : 0}
            </span>
          ),
        },
        // { label: "Job Description", field: "job_description" },
        { label: "Duration (Days)", field: "duration_days" },
      ];

      // Only add Actions column if user is HR (excluding Manager)
      if (isHR && !isManager) {
        baseColumns.push({
          label: "Actions",
          field: "actions",
          render: (row) => (
            <div className="flex items-center justify-center">
              <div className="relative dropdown-container">
                <button
                  ref={(el) => {
                    if (el) buttonRefs.current[row.id] = el;
                  }}
                  onClick={(e) => handleDropdownToggle(row.id, e)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {openDropdownId === row.id &&
                createPortal(
                  <div
                    style={{
                      position: "fixed",
                      top: dropdownCoords.y,
                      left: dropdownCoords.x,
                      zIndex: 9999,
                    }}
                    className="dropdown-menu bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[150px]"
                  >
                    <div className="flex flex-col space-y-0">
                      {row.status !== "Close" && (
                        <button
                          onClick={() => {
                            openModalForEdit(row);
                            setOpenDropdownId(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                          aria-label={`Edit ${row.campaign_name}`}
                        >
                          Edit
                        </button>
                      )}
                      <DeleteButton
                        style={`text-left text-red-600 text-sm hover:text-red-800 px-3 py-2`}
                        onDelete={() => {
                          handleDelete(row.id);
                          setOpenDropdownId(null);
                        }}
                      />
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          ),
        });
      }

      return baseColumns;
    };
  }, [
    handleDelete,
    openModalForEdit,
    handleStatusChange,
    campaignLoading,
    copied,
    isHR,
    isManager,
  ]);

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Campaigns
        </h2>
        {!isManager && (
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 font-medium rounded-md hover:bg-blue-700 w-full sm:w-auto"
          >
            Create New Campaign
          </button>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">
        {/* Search field */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full placeholder:text-xs dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 md:placeholder:text-sm p-2 pr-8 border border-gray-300 rounded-md"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Campaign filter */}
        <div className="w-full sm:w-64">
          <select
            value={selectedCampaignFilter}
            onChange={(e) => setSelectedCampaignFilter(e.target.value)}
            className="w-full p-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none text-sm border border-gray-300 rounded-md"
            disabled={loadingCampaignNames}
          >
            <option value="">All Campaigns</option>
            {campaignNames.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.campaign_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Campaigns Content */}
      <div>
        <ReusableTable
          data={displayCampaigns}
          columns={getColumns(
            activePagination.currentPage,
            activePagination.perPage
          )}
          loading={loading || isSearching}
          filterable={false}
        />

        {/* Pagination */}
        {displayCampaigns?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={activePagination.currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={activePagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingCampaign ? "Edit Campaign" : "Create New Campaign"}
        fullSize={false}
      >
        <CampaignForm
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingCampaign || {}}
          mode={editingCampaign ? "Edit" : "Add"}
        />
      </Modal>
    </div>
  );
};

export default React.memo(Campaigns);
